import p5 from 'p5'
import { BaseMarker } from './markers/base/baseMarker'
import { BezierLineMarker } from './markers/bezier/bezierLineMarker'
import { PolygonMarker } from './markers/polygon/polygonMarker'
import { RectangleMarker } from './markers/rectangle/rectangleMarker'
import { MeasurementMarker } from './markers/measurement/measurementMarker'
import { SelectionMarker } from './markers/selection/selectionMarker'
import { getIntersectingMarkers, inverseApplyMatrix } from './helperFunctions'
import { IEventListenerRepository, EventListenerRepository, EventHandler, MarkerEvent } from './events'
import {
  MarkerModes,
  MarkerState,
  type MarkerConstructor,
  type MarkerTypeName,
  type TransformMatrixType,
} from './types'
import { CURSOR_SVG_STRING } from './constants'

export const MARKER_CLASSES: Record<string, MarkerConstructor> = {
  [BezierLineMarker.typeName]: BezierLineMarker,
  [RectangleMarker.typeName]: RectangleMarker,
  [PolygonMarker.typeName]: PolygonMarker,
  [MeasurementMarker.typeName]: MeasurementMarker,
  [SelectionMarker.typeName]: SelectionMarker,
}

interface IMarkerArea {
  canvas: HTMLCanvasElement
  width: number
  height: number
  p: p5
  _pixelRatio: number
  _transformMatrix: TransformMatrixType
  baseTransformMatrix: TransformMatrixType
  boundingBox: { width: number; height: number }
  isInteractive: boolean
  shouldRender: boolean
  showGrid: boolean
  isCtrlPressed: boolean
  intersectedMarkers?: Array<BaseMarker<any, any, any, any>>
  selectedMarkers?: Array<BaseMarker<any, any, any, any>>
  currentMarker?: BaseMarker<any, any, any, any>
  hoveredMarker?: BaseMarker<any, any, any, any>
  markers: Array<BaseMarker<any, any, any, any>>
  eventListeners: EventListenerRepository
  scaleFactor: number
  open: () => void
  render: () => void
  setup: (p: p5) => void
  pause: () => void
  close: () => void
  scaleMarkers: (scaleX: number, scaleY: number) => void
  createNewMarker: (markerType: any) => void
  loadMarker: (marker: MarkerState) => void
  removeMarker: (marker: BaseMarker<any, any, any, any>) => void
  loadMarkers: (markers: Array<MarkerState>) => void
  clearMarkers: () => void
  bindMarkerCallbacks: (marker: BaseMarker<any, any, any, any>) => void
  set pixelRatio(newPixelRatio: number)
  get pixelRatio(): number
  set transformMatrix(newTransformMatrix: TransformMatrixType)
  get transformMatrix(): TransformMatrixType
  get isOpen(): boolean
  get markerTypes(): Array<MarkerConstructor>
}

class MarkerArea implements IMarkerArea {
  canvas: HTMLCanvasElement
  width: number
  height: number
  p: p5
  _pixelRatio = window.devicePixelRatio
  _transformMatrix: TransformMatrixType = [1, 0, 0, 1, 0, 0]
  baseTransformMatrix: TransformMatrixType = [0, 0, 0, 0, 0, 0]
  boundingBox: { width: number; height: number }
  isInteractive: boolean
  isCtrlPressed = false
  private _wasGestureAborted = false
  shouldRender = false
  showGrid = false
  intersectedMarkers = []
  selectedMarkers = []
  currentMarker: BaseMarker<any, any, any, any> | undefined = undefined
  hoveredMarker?: BaseMarker<any, any, any, any>
  markers: Array<BaseMarker<any, any, any, any>> = []
  eventListeners = new EventListenerRepository()
  scaleFactor = 1

  constructor(
    canvas: HTMLCanvasElement,
    height: number,
    width: number,
    options: { isInteractive?: boolean } = { isInteractive: true }
  ) {
    this.canvas = canvas
    this.height = height
    this.width = width
    this.isInteractive = options.isInteractive
    this.boundingBox = { width: height, height: width }
    this.setup = this.setup.bind(this)
    this.bindMarkerCallbacks = this.bindMarkerCallbacks.bind(this)
    this.canvasContainsTarget = this.canvasContainsTarget.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
  }

  private updateScaleFactor() {
    //zero position is scaleX, which should always match the scaleY value stored in the fourh position
    this.scaleFactor = 1 / this.transformMatrix[0]
  }

  /**
   * Creates a new p5 instance and calls the setup function to intitialize render loop
   * @returns void
   */
  open() {
    this.p = new p5(this.setup)
    //TODO Resolve typing for custom props
    //TODO Wrap p in gfx class
    this.p.localMousePosition = () => {
      const m = this.transformMatrix
      const inversedMousePosition = inverseApplyMatrix(this.p.mouseX, this.p.mouseY, m)

      return {
        x: inversedMousePosition.x,
        y: inversedMousePosition.y,
      }
    }
    this.attachEvents()
  }

  /**
   * starts the rendering loop for * the p5 instance.
   * @returns void
   */
  render() {
    if (this.shouldRender === false) {
      this.shouldRender = true
      if (this.p) this.p.loop()
    }
  }

  mouseWithinBounds() {
    const mousePosition = this.p.localMousePosition()

    const mouseX = mousePosition.x
    const mouseY = mousePosition.y

    const isMouseWithinBounds =
      mouseX >= 0 && mouseX <= this.boundingBox.width && mouseY >= 0 && mouseY <= this.boundingBox.height

    return isMouseWithinBounds
  }

  canvasContainsTarget(e: Event): boolean {
    return this.canvas.contains(e.target as Node)
  }

  isMarkerInteractionAllowed(e): boolean {
    const containsTarget = this.canvasContainsTarget(e)
    return (
      this.isInteractive &&
      (this.selectedMarkers.length > 0 || this.currentMarker) &&
      this.mouseWithinBounds() &&
      containsTarget
    )
  }

  drawGrid() {
    if (this.showGrid) {
      this.p.stroke(128, 128, 128, 150)
      this.p.strokeWeight(0.5 * this.scaleFactor)
      for (let i = 0; i <= this.boundingBox.width; i++) {
        this.p.line(i, 0, i, this.boundingBox.width)
      }
      for (let i = 0; i <= this.boundingBox.height; i++) {
        this.p.line(0, i, this.boundingBox.height, i)
      }
    }
  }

  /**
   * Sets up the p5 render
   */
  setup(p: p5) {
    p.setup = () => {
      p.createCanvas(this.width, this.height, p.P2D, this.canvas)
      p.pixelDensity(this.pixelRatio)
    }

    p.draw = () => {
      p.clear(0, 0, 0, 0)
      if (this.shouldRender) {
        this.applyTransform()
        this.drawGrid()
        this.markers.forEach((marker) => marker.render())
      }
    }

    p.mouseClicked = (e: PointerEvent) => {
      const containsTarget = this.canvasContainsTarget(e)
      if (this.isInteractive && this.mouseWithinBounds() && containsTarget) {
        const closestMarker = this.getClosestMarker()
        const isAnySelectedMarkerMousedOver = this.selectedMarkers.find((marker) => marker.isMousedOver()) !== undefined
        const isMarkerBeingCreated = this.currentMarker?.state.mode === MarkerModes.Create

        //Handle Virtual Selection via markers intersected
        if (this.intersectedMarkers.length > 0) {
          this.intersectedMarkers.forEach((marker) => marker.controller.selectMarker())
          this.intersectedMarkers = []
          return
        }

        //if no markers are moused over - deselect all markers
        if (!isAnySelectedMarkerMousedOver && !this.isCtrlPressed && this.selectedMarkers.length > 0) {
          this.selectedMarkers.forEach((marker) => marker.controller.deselectMarker())
        }
        if (closestMarker && !isMarkerBeingCreated) {
          closestMarker.controller.mouseClickedEvent(e)
        } else if (this.currentMarker) {
          this.currentMarker.controller.mouseClickedEvent(e)
        }
      }
    }

    p.mouseDragged = (e: MouseEvent) => {
      const containsTarget = this.canvasContainsTarget(e)
      const isInteractionAllowed = this.isInteractive && containsTarget && this.mouseWithinBounds()

      // if selection is already active, do not re-run creation logic
      const isSelectionActive = this.currentMarker instanceof SelectionMarker
      if (isSelectionActive) {
        this.currentMarker.controller.mouseDraggedEvent()
        return
      }

      const markerSelected = this.selectedMarkers.length > 0 || this.currentMarker

      if (isInteractionAllowed) {
        if (markerSelected) {
          const closestMarker = this.getClosestMarker()
          const isClosestMarkerSelected = this.selectedMarkers.find((marker) => closestMarker === marker) !== undefined
          const isMarkerBeingCreated = this.currentMarker?.state.mode === MarkerModes.Create

          if (isClosestMarkerSelected && !isMarkerBeingCreated) closestMarker.controller.mouseDraggedEvent()
          else if (this.currentMarker) this.currentMarker.controller.mouseDraggedEvent()
        } else if (!this._wasGestureAborted) {
          const selectionMarker = this.createNewMarker(SelectionMarker.typeName)
          selectionMarker.controller.mousePressedEvent()
        }
      }
    }

    p.mouseMoved = (e: MouseEvent) => {
      const containsTarget = this.canvasContainsTarget(e)

      if (this.isInteractive && this.mouseWithinBounds() && containsTarget) {
        //TODO Implement closest marker logic to
        //improve hover states
        //Currently if you hover a marker inside a marker
        //both will show in a hovered state.
        //Ideally the closest marker is shown in a
        //hovered state
        this.markers.forEach((marker) => {
          marker.controller.mouseMovedEvent()
        })
      }
    }

    p.mousePressed = (e: MouseEvent) => {
      this._wasGestureAborted = false

      if (this.isMarkerInteractionAllowed(e)) {
        const closestMarker = this.getClosestMarker()
        const closestMarkerSelected = this.selectedMarkers.find((marker) => closestMarker === marker) !== undefined
        const isMarkerBeingCreated = this.currentMarker?.state.mode === MarkerModes.Create

        if (closestMarkerSelected) closestMarker.controller.mousePressedEvent()
        if (isMarkerBeingCreated) this.currentMarker.controller.mousePressedEvent()
      }
    }

    p.mouseReleased = (e: MouseEvent) => {
      if (this.isMarkerInteractionAllowed(e)) {
        const closestMarker = this.getClosestMarker()
        const closestMarkerSelected = this.selectedMarkers.find((marker) => closestMarker === marker) !== undefined
        const isMarkerBeingCreated = this.currentMarker?.state.mode === MarkerModes.Create

        if (closestMarkerSelected) closestMarker.controller.mouseReleasedEvent()
        if (isMarkerBeingCreated) this.currentMarker.controller.mouseReleasedEvent()
      }
    }

    p.keyPressed = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.abortMarkerCreation()
        this._wasGestureAborted = true
        return
      }

      if (this.isMarkerInteractionAllowed(e)) {
        if (typeof this.currentMarker?.controller?.keyPressedEvent === 'function')
          this.currentMarker.controller.keyPressedEvent()
      }
    }
  }

  getClosestMarker() {
    const mousedOverMarkers: Array<BaseMarker<any, any, any, any>> = []
    let closestMarker
    let closestMarkerDistance = Infinity

    this.markers.forEach((marker) => {
      if (marker.isMousedOver() || marker.state.isPressed) {
        mousedOverMarkers.push(marker)
      }
    })

    mousedOverMarkers.forEach((marker) => {
      const mouseToMarkerdistance = marker.getMouseToShapeDistance()
      if (mouseToMarkerdistance < closestMarkerDistance) {
        closestMarkerDistance = mouseToMarkerdistance
        closestMarker = marker
      }
    })

    return closestMarker
  }

  /**
   * Stops the rendering loop for the p5 instance
   * @returns void
   */
  pause() {
    if (this.shouldRender === true) {
      this.shouldRender = false
      if (this.p) this.p.noLoop()
    }
  }

  close() {
    this.pause() //Stop rendering loop before disposing of resources
    if (this.p) this.p.remove()
    this.p = undefined
    this.detachEvents()
  }

  /**
   * Applies the transforms as defined in the transform matrix to the current p5 instance
   */
  applyTransform() {
    const m = this.transformMatrix
    this.p.applyMatrix(m[0], m[1], m[2], m[3], m[4], m[5])
  }

  /**
   * Resizes the markerArea to the provided height and width
   */
  resize(newHeight: number, newWidth: number) {
    this.height = newHeight
    this.width = newWidth
    if (this.p) {
      this.p.height = newHeight
      this.p.width = newWidth
    }
  }

  updateCursor() {
    const isMarkerBeingCreated = this.currentMarker?.state.mode === MarkerModes.Create
    const isCurrentMarkerHovered = this.currentMarker?.state.isHovered
    const isMarkerHovered = this.hoveredMarker?.state.isHovered === true && this.currentMarker !== this.hoveredMarker
    const isMarkerSelected = this.currentMarker?.state.isSelected === true

    const showCrosshair = isMarkerBeingCreated
    const showPointer = isMarkerHovered
    const showMove = isMarkerSelected && isCurrentMarkerHovered

    const encodedCursor = 'data:image/svg+xml;base64,' + btoa(CURSOR_SVG_STRING)
    const fallbackCursor = 'move'

    switch (true) {
      case showCrosshair:
        this.canvas.style.cursor = 'crosshair'
        break
      case showPointer:
        this.canvas.style.cursor = 'pointer'
        break
      case showMove:
        this.canvas.style.cursor = `url(${encodedCursor}), ${fallbackCursor}`
        break
      default:
        this.canvas.style.cursor = 'default'
        break
    }
  }

  /**
   * Scales the markers the given scale
   */
  scaleMarkers() {
    this.markers.forEach((marker) => {
      marker.state.scalingRatio = this.scaleFactor
      marker.state.handles.forEach((handle) => {
        handle.size = marker.state.handleRadius
        handle.strokeWeight = marker.state.handleStrokeWeight
      })
    })
  }

  /**
   * Create a new marker of the provided type, adds it to the list of existing markers,
   * and emits an event that a new marker was created.
   */
  createNewMarker(markerType: MarkerTypeName) {
    let marker
    if (typeof markerType === 'string') {
      const MarkerConstructor = MARKER_CLASSES[markerType]
      marker = new MarkerConstructor()
      marker.p = this.p
      marker.state.mode = MarkerModes.Create
      marker.state.scaleFactor = this.scaleFactor

      if (markerType !== SelectionMarker.typeName) this.bindMarkerCallbacks(marker)
      else this.bindSelectionMarkerCallbacks(marker)

      this.markers.push(marker)
      this.currentMarker = marker
    }
    this.updateCursor()
    this.scaleMarkers()
    return marker
  }

  /**
   * Loads a marker of the provided type into state and binds all relevant event listeners
   */
  loadMarker(state: MarkerState): BaseMarker<any, any, any, any> {
    const MarkerConstructor = MARKER_CLASSES[state.typeName]
    const marker = new MarkerConstructor()
    if (this.p) marker.p = this.p
    marker.state.scalingRatio = this.scaleFactor
    marker.state.mode = MarkerModes.Display
    marker.state.restore(state)
    this.bindMarkerCallbacks(marker)
    this.markers.push(marker)
    return marker
  }

  /**
   * Removes the marker currently being created
   */
  abortMarkerCreation() {
    if (this.currentMarker?.state.mode === MarkerModes.Create) {
      const marker = this.markers.pop()
      this.currentMarker = null
      this.removeSelectedMarker(marker)
      this.updateCursor()
    }
  }

  /**
   * Removes the marker from the internal markerarea state
   */
  removeMarker(marker: BaseMarker<any, any, any, any>) {
    this.markers = this.markers.filter((_marker) => {
      return _marker !== marker
    })
    this.selectedMarkers = this.selectedMarkers.filter((_marker) => {
      return _marker !== marker
    })
    if (this.currentMarker === marker) this.currentMarker = null
  }

  /**
   * Loads a list of markers into state and binds all relevant event listeners for each marker.
   */
  loadMarkers(markerStates: Array<MarkerState>) {
    markerStates.forEach((state) => this.loadMarker(state))
  }

  /**
   * Removes all markers.
   */
  clearMarkers() {
    this.markers.length = 0
    this.currentMarker = undefined
    this.updateCursor()
  }

  addEventListener<T extends keyof IEventListenerRepository>(eventType: T, handler: EventHandler<T>): void {
    this.eventListeners.addEventListener(eventType, handler)
  }

  removeEventListener<T extends keyof IEventListenerRepository>(eventType: T, handler: EventHandler<T>): void {
    this.eventListeners.removeEventListener(eventType, handler)
  }

  addSelectedMarker(marker: BaseMarker<any, any, any, any>) {
    this.selectedMarkers.push(marker)
  }

  removeSelectedMarker(marker: BaseMarker<any, any, any, any>) {
    this.selectedMarkers = this.selectedMarkers.filter((_marker) => _marker !== marker)
  }

  /**
   * Binds the callbacks for the provided marker
   */
  bindMarkerCallbacks(marker: BaseMarker<any, any, any, any>) {
    //TODO Improve markerMousedenter and mouseLeave
    //logic as entering a the area of a marker within
    //a marker never deselects the existing marker
    //Part of the issue is that - if a marker is within
    //a marker, the first marker entered is never "exited"
    //Once the mouse leaves the internal marker
    //the hoveredMarker is set to null even though
    //the external marker is still hovered

    marker.markerMouseEnterCallback = () => {
      this.hoveredMarker = marker
      this.updateCursor()
      this.eventListeners['markermouseenter']?.forEach((listener) => {
        if (typeof listener === 'function') listener(new MarkerEvent(this, marker))
      })
    }

    marker.markerMouseLeaveCallback = () => {
      this.updateCursor()
      this.hoveredMarker = null
      this.eventListeners['markermouseleave']?.forEach((listener) => {
        if (typeof listener === 'function') listener(new MarkerEvent(this, marker))
      })
    }

    marker.markerSelectedCallback = () => {
      this.currentMarker = marker
      this.addSelectedMarker(marker)
      this.updateCursor()
      this.eventListeners['markerselect']?.forEach((listener) => {
        if (typeof listener === 'function') listener(new MarkerEvent(this, marker))
      })
    }
    marker.markerDeselectedCallback = () => {
      this.updateCursor()
      if (this.currentMarker === marker) this.currentMarker = null
      this.removeSelectedMarker(marker)
      this.eventListeners['markerdeselect']?.forEach((listener) => {
        if (typeof listener === 'function') listener(new MarkerEvent(this, marker))
      })
    }
    marker.markerCreatedCallback = () => {
      this.currentMarker = marker
      this.updateCursor()
      this.eventListeners['markercreate']?.forEach((listener) => {
        if (typeof listener === 'function') listener(new MarkerEvent(this, marker))
      })
    }
    marker.markerEditCallback = () => {
      this.eventListeners['markerchange']?.forEach((listener) => {
        if (typeof listener === 'function') listener(new MarkerEvent(this, marker))
      })
    }
  }

  /**
   * Binds the callbacks for selection marker
   */
  bindSelectionMarkerCallbacks(marker: SelectionMarker) {
    marker.markerCreatedCallback = () => {
      this.updateCursor()
      this.removeMarker(marker)
    }
    marker.markerEditCallback = () => {
      // Step 1: Get the new intersecting markers as a Set
      const newIntersectingMarkers = new Set(getIntersectingMarkers(marker, this.markers))

      // Step 2: Convert the current intersectedMarkers to a Set for O(1) lookups
      const currentIntersectedMarkers = new Set(this.intersectedMarkers)

      // Step 3: Determine markers to leave (present in current but not in new)
      currentIntersectedMarkers.forEach((marker) => {
        if (!newIntersectingMarkers.has(marker)) {
          marker.controller.leaveMarker()
        }
      })

      // Step 4: Determine markers to enter (present in new but not in current)
      newIntersectingMarkers.forEach((marker) => {
        if (!currentIntersectedMarkers.has(marker)) {
          marker.controller.enterMarker()
        }
      })

      // Step 5: Update the intersectedMarkers to the new set
      this.intersectedMarkers = Array.from(newIntersectingMarkers)
    }
  }

  combineMatrices(transformMatrix, baseMatrix) {
    // Extract elements from matrices
    const [aT, bT, cT, dT, eT, fT] = transformMatrix
    const [aB, bB, cB, dB, eB, fB] = baseMatrix

    // Calculate new scale factors by multiplying the corresponding scales
    const aNew = aT * aB
    const dNew = dT * dB

    // Apply scaling from baseMatrix to translations of transformMatrix, then add baseMatrix translations
    const eNew = eT + eB
    const fNew = fT + fB

    // Since skewing and other transformations are linear and can be simply added
    const bNew = bT + bB
    const cNew = cT + cB

    return [aNew, bNew, cNew, dNew, eNew, fNew]
  }

  attachEvents() {
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('keyup', this.onKeyUp)
  }

  detachEvents() {
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('keyup', this.onKeyUp)
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.ctrlKey) {
      this.isCtrlPressed = true
    }
  }

  onKeyUp(e: KeyboardEvent) {
    if (e.ctrlKey === false) {
      this.isCtrlPressed = false
    }
  }

  /**
   * Gets array of Marker Types
   */
  get markerTypes(): Array<MarkerConstructor> {
    return [BezierLineMarker, PolygonMarker, RectangleMarker]
  }

  /**
   * Whether the p5 instance is bound to the canvas element and if the markers are being
   * rendered to it.
   */
  get isOpen() {
    return this.p !== undefined && this.p.isLooping()
  }

  set pixelRatio(newPixelRatio: number) {
    this._pixelRatio = newPixelRatio
    if (this.p) {
      this.p.pixelDensity(this.pixelRatio)
    }
  }

  get pixelRatio() {
    return this._pixelRatio
  }

  set transformMatrix(newTransformMatrix: TransformMatrixType) {
    const baseTransformAppliedMatrix = this.combineMatrices(newTransformMatrix, this.baseTransformMatrix)
    this._transformMatrix = baseTransformAppliedMatrix as TransformMatrixType
    this.updateScaleFactor()
    this.scaleMarkers()
  }
  get transformMatrix(): TransformMatrixType {
    return this._transformMatrix
  }
}

export type { IMarkerArea }
export { MarkerArea }
